import {
  getLastWeekNumberInTheYear,
  getWeeksFromDate,
  isNullish,
  utcDateTime,
} from '@epicuro-next/utilities';
import { createSelector } from '@ngrx/store';

import { InventoryItem, ProductSizeEntity } from '../models';
import { unpackNowDate } from '../shared/unpack-now-date';

import { selectSizeEntities } from './data/config-strip.data.selectors';
import {
  availableInventoriesForSelectedSizeForOrderType,
  availableInventoriesQuantitiesForDeliveryPeriod,
} from './shared/inventory-overview';
import { selectCurrentlyActiveOrderType } from './ui/config-strip.ui.selectors';

const DATE_SEPARATOR = '|';

type IndexedObject<T> = {
  [index: string]: T;
};
type InventoryQuantity = number | null | undefined;

type WeekInventory = IndexedObject<number>;

export const selectFirstAvailableSizeInventoriesForSelectedOrderType =
  createSelector(
    selectSizeEntities,
    selectCurrentlyActiveOrderType,
    (sizes, orderType) => {
      const inventoryItems = Object.entries(sizes)
        .filter(([_, size]: [string, ProductSizeEntity]) =>
          (size.inventoryItems ?? []).some(
            (inventory) => inventory.orderType === orderType,
          ),
        )
        .reduce(
          (acc, [sizeId, curr]: [string, ProductSizeEntity]) => [
            ...acc,
            ...(curr.inventoryItems ?? []).map((invItem) => ({
              ...invItem,
              sizeId,
            })),
          ],
          [],
        );

      const sorted = getDateRangeQuantitiesForSizes(inventoryItems);

      // find range where every size is unlimited or unavailable
      const targetDateRanges = Object.keys(sorted).filter((date) =>
        Object.values(sorted[date]).every((quantity) => isNullish(quantity)),
      );

      if (!targetDateRanges.length) {
        return [];
      }

      return inventoryItems.filter((inv) =>
        targetDateRanges.find(
          (range) =>
            range ===
            `${
              inv.displayFromDateUtc ?? inv.availableFromDateUtc
            }${DATE_SEPARATOR}${
              inv.displayToDateUtc ?? inv.availableToDateUtc
            }`,
        ),
      );
    },
  );

function getDateRangeQuantitiesForSizes(
  inventories: (InventoryItem & { sizeId: string })[],
): IndexedObject<IndexedObject<InventoryQuantity>> {
  // collect inventory ranges to check between
  const dateRanges = new Set(
    inventories.map(
      (inv) =>
        `${
          inv.displayFromDateUtc ?? inv.availableFromDateUtc
        }${DATE_SEPARATOR}${inv.displayToDateUtc ?? inv.availableToDateUtc}`,
    ),
  );

  // set a map of every available size
  // undefined is for indicating that size does not have any inventories
  const sizeIdsMap: IndexedObject<InventoryQuantity> = inventories.reduce(
    (acc, curr) => ({ ...acc, [curr.sizeId]: undefined }),
    {},
  );

  // assign a size map to every date range
  const map: IndexedObject<IndexedObject<number | null | undefined>> = [
    ...dateRanges,
  ].reduce(
    (acc, curr) => ({
      ...acc,
      [curr]: sizeIdsMap,
    }),
    {},
  );

  dateRanges.forEach((range: string) => {
    const [from, to] = range
      .split(DATE_SEPARATOR)
      .map((date) => new Date(date));
    inventories.forEach((inventory) => {
      // check every inventory and see the inventory ranges it overlaps with
      const invFrom = new Date(
        inventory.displayFromDateUtc ?? inventory.availableFromDateUtc,
      );
      const invTo = new Date(
        inventory.displayToDateUtc ?? inventory.availableToDateUtc,
      );

      if (invFrom <= from && invTo >= to) {
        const currentQuantity = map[range][inventory.sizeId];

        // then check the maximum quantity available for size in this range
        // since this logic is needed only for unlimited quantites, we check only for null
        map[range] = {
          ...map[range],
          [inventory.sizeId]:
            currentQuantity === null ? currentQuantity : inventory.quantity,
        };
      }
    });
  });

  return map;
}

const selectAvailableInventoriesForSelectedSizeForOrderType = createSelector(
  selectFirstAvailableSizeInventoriesForSelectedOrderType,
  availableInventoriesForSelectedSizeForOrderType,
);

const selectAvailableInventoriesQuantitiesForDeliveryPeriod = createSelector(
  selectAvailableInventoriesForSelectedSizeForOrderType,
  availableInventoriesQuantitiesForDeliveryPeriod,
);

export const selectAvailableInventoriesQuatntitiesPerWeekPeriod =
  createSelector(
    selectAvailableInventoriesQuantitiesForDeliveryPeriod,
    (availableQuantities) => {
      return Object.values(availableQuantities).reduce(
        (acc, { deliveryPeriod, availableTo, maxQuantity }) => {
          const utcStartDate = utcDateTime(unpackNowDate(deliveryPeriod));
          const utcEndDate = utcDateTime(availableTo);
          const startDate = getWeeksFromDate(
            1,
            utcStartDate.get('year'),
            utcStartDate.get('month'),
            utcStartDate.get('day'),
          )[0];
          const endDate = getWeeksFromDate(
            1,
            utcEndDate.get('year'),
            utcEndDate.get('month'),
            utcEndDate.get('day'),
          )[0];
          let startWeek: number = startDate?.week;
          const endWeek: number = endDate?.week;

          const startYear = startDate?.year;
          const endYear = endDate?.year;

          let currentWithPrevPeriod = {};

          if (endWeek < startWeek && endYear <= startYear) {
            const lastWeekOfAYear = getLastWeekNumberInTheYear(
              utcStartDate.get('year'),
            );

            currentWithPrevPeriod = getWeekInventories(
              lastWeekOfAYear,
              startWeek,
              startYear,
              maxQuantity,
              currentWithPrevPeriod,
              acc,
            );
            startWeek = 0;
          }

          // need to handle inventories across years
          if (endYear > startYear) {
            currentWithPrevPeriod = getWeekAcrossYears(
              startWeek,
              startYear,
              endWeek,
              endYear,
              maxQuantity,
              currentWithPrevPeriod,
              acc,
            );
          }

          currentWithPrevPeriod = getWeekInventories(
            endWeek,
            startWeek,
            startYear,
            maxQuantity,
            currentWithPrevPeriod,
            acc,
          );

          return {
            ...acc,
            ...currentWithPrevPeriod,
          };
        },
        {} as WeekInventory,
      );
    },
  );

// this is needed when availability ends in the next year
function getWeekAcrossYears(
  startWeek: number,
  startYear: number,
  endWeek: number,
  endYear: number,
  maxQuantity: number,
  initial: WeekInventory,
  acc: WeekInventory,
) {
  const yearGap = endYear - startYear - 1 > 2 ? 2 : endYear - startYear - 1;

  const gapWeeks = Array.from({ length: yearGap }, (_, yearIndex) => {
    const weeks = getLastWeekNumberInTheYear(startYear + yearIndex + 1);
    return Array.from({ length: weeks }, (__, weekNo) => ({
      [weekNo + 1 + '-' + (startYear + yearIndex + 1)]:
        maxQuantity + (acc[weekNo + 1 + '-' + endYear] ?? 0),
    }));
  }).flat();

  const lastWeekOfStartYear = getLastWeekNumberInTheYear(startYear);
  const startYearWeeks = lastWeekOfStartYear - startWeek;

  const firstYear = Array.from({ length: startYearWeeks + 1 }, (_, i) => ({
    [i + startWeek + '-' + startYear]:
      maxQuantity + (acc[i + startWeek + '-' + startYear] ?? 0),
  }));

  const lastYear = Array.from({ length: endWeek }, (_, i) => ({
    [i + 1 + '-' + endYear]: maxQuantity + (acc[i + 1 + '-' + endYear] ?? 0),
  }));

  return [...firstYear, ...gapWeeks, ...lastYear].reduce(
    (weeksObj, curr) => ({ ...weeksObj, ...curr }),
    initial,
  );
}

function getWeekInventories(
  endWeek: number,
  startWeek: number,
  year: number,
  maxQuantity: number,
  initial: WeekInventory,
  acc: WeekInventory,
) {
  return (
    Array
      // +1 due to the fact that length starts from 0
      .from({ length: endWeek - startWeek + 1 }, (_, i) => ({
        [i + startWeek + '-' + year]:
          maxQuantity + (acc[i + startWeek + '-' + year] ?? 0),
      }))
      .reduce((weeksObj, curr) => ({ ...weeksObj, ...curr }), initial)
  );
}
